import React, { useEffect, useMemo, useState } from 'react';
import { Col, ConfigProvider, Divider, Drawer, Flex, Form, Input, InputNumber, Row, Select, Space, Spin, message, DatePicker } from 'antd';
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';
import type { Company, ConstMachines, VwPersonnel } from '../../../types';
import { fetchSqlDataWithQuery } from '../../../services/api';
import ContractSelect from '../../../components/modals/ContractSelect';
import Button from '../../../components/common/Button';

interface MachConstMachineFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: Partial<ConstMachines>) => void;
  initialValues?: ConstMachines | null;
  companies: Company[];
  personnel: VwPersonnel[];
  isLoadingLookups: boolean;
  onMachineTypesChange?: (types: string[]) => void;
}

type SpecRow = { key: string; value: string };

const EMPTY_SPEC: SpecRow = { key: '', value: '' };

const matchOption = (input: string, option?: { label?: string }) =>
  (option?.label ?? '').toLowerCase().includes(input.toLowerCase());

const MachConstMachineForm: React.FC<MachConstMachineFormProps> = ({
  open,
  onClose,
  onSubmit,
  initialValues,
  companies,
  personnel: allPersonnel,
  isLoadingLookups,
  onMachineTypesChange,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [contractModalOpen, setContractModalOpen] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null | undefined>(initialValues?.company_id);
  const [specRows, setSpecRows] = useState<SpecRow[]>([EMPTY_SPEC]);
  const contractReference = Form.useWatch('contract_reference_id', form);

  const { data: machineTypeEnumRows = [], isLoading: isLoadingMachineTypes } = useQuery<any[], Error>({
    queryKey: ['enum', 'machine_type_enum'],
    queryFn: () => fetchSqlDataWithQuery('SELECT unnest(enum_range(NULL::machine_type_enum)) AS value'),
    staleTime: 1000 * 60 * 30,
  });

  const machineTypeOptions = useMemo(() => {
    const rows: any[] = Array.isArray(machineTypeEnumRows) ? machineTypeEnumRows : [];
    return rows.map((row) => ({ value: row.value, label: row.value }));
  }, [machineTypeEnumRows]);

  useEffect(() => {
    if (!onMachineTypesChange) return;
    onMachineTypesChange(
      machineTypeOptions
        .map((option) => String(option.value ?? '').trim())
        .filter(Boolean)
    );
  }, [machineTypeOptions, onMachineTypesChange]);

  const ownershipOptions = [
    { value: 'CompanyOwned', label: 'Company Owned' },
    { value: 'Rented', label: 'Rented' },
    { value: 'Leased', label: 'Leased' },
  ];
  const statusOptions = [
    { value: 'Available', label: 'Available' },
    { value: 'Assigned', label: 'Assigned' },
    { value: 'InMaintenance', label: 'In Maintenance' },
    { value: 'OutOfService', label: 'Out of Service' },
    { value: 'Inactive', label: 'Inactive' },
  ];
  const fuelTypeOptions = [
    { value: 'diesel', label: 'Diesel' },
    { value: 'gasoline', label: 'Gasoline' },
    { value: 'electric', label: 'Electric' },
  ];

  const filteredPersonnel = useMemo(
    () => (selectedCompanyId ? allPersonnel.filter((person) => person.company_id === selectedCompanyId) : []),
    [selectedCompanyId, allPersonnel],
  );

  useEffect(() => {
    if (!selectedCompanyId) return;
    const currentOperatorId = form.getFieldValue('operator_id');
    if (currentOperatorId && !filteredPersonnel.some((person) => person.id === currentOperatorId)) {
      form.setFieldsValue({ operator_id: undefined });
    }
  }, [selectedCompanyId, filteredPersonnel, form]);

  const handleCompanyChange = (value: number | undefined) => {
    setSelectedCompanyId(value);
    form.setFieldsValue({ operator_id: undefined });
  };

  useEffect(() => {
    if (!open) return;

    if (initialValues) {
      form.setFieldsValue({
        ...initialValues,
        year: initialValues.year ?? undefined,
        kilometer: initialValues.kilometer ?? undefined,
        hours_counter: initialValues.hours_counter ?? 0,
        company_id: initialValues.company_id ?? undefined,
        operator_id: initialValues.operator_id ?? undefined,
        fuel_type: initialValues.fuel_type ?? undefined,
        ownership_type: initialValues.ownership_type ?? undefined,
        contract_reference_id: initialValues.contract_reference_id ?? undefined,
        arrival_date: initialValues.arrival_date ? dayjs(initialValues.arrival_date) : undefined,
        departure_date: initialValues.departure_date ? dayjs(initialValues.departure_date) : undefined,
        status: initialValues.departure_date ? 'Inactive' : initialValues.status ?? undefined,
      });
      setSelectedCompanyId(initialValues.company_id);
      const specs = initialValues.specs;
      const rows = specs && typeof specs === 'object'
        ? Object.entries(specs)
          .filter(([key]) => key)
          .map(([key, value]) => ({ key, value: value != null ? String(value) : '' }))
        : [];
      setSpecRows(rows.length > 0 ? rows : [EMPTY_SPEC]);
      return;
    }

    form.resetFields();
    form.setFieldsValue({ status: 'Available', ownership_type: 'CompanyOwned', hours_counter: 0 });
    setSelectedCompanyId(null);
    setSpecRows([EMPTY_SPEC]);
  }, [open, initialValues, form]);

  const updateSpec = (index: number, patch: Partial<SpecRow>) => {
    setSpecRows((rows) => rows.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)));
  };

  const handleFinish = async (values: Partial<ConstMachines> & { arrival_date?: Dayjs | string; departure_date?: Dayjs | string | null }) => {
    setLoading(true);
    try {
      const specs: Record<string, string | null> = {};
      specRows.forEach((row) => {
        const key = row.key.trim();
        if (!key) return;
        specs[key] = row.value.trim() || null;
      });
      const arrival = values.arrival_date;
      const departure = values.departure_date;
      await onSubmit({
        ...values,
        arrival_date: dayjs.isDayjs(arrival) ? arrival.format('YYYY-MM-DD') : arrival,
        departure_date: departure ? (dayjs.isDayjs(departure) ? departure.format('YYYY-MM-DD') : departure) : null,
        status: departure ? 'Inactive' : values.status,
        specs: Object.keys(specs).length > 0 ? specs : null,
      });
    } catch (err: any) {
      message.error(`Submit failed: ${err.message || 'Please check the fields'}`);
    } finally {
      setLoading(false);
    }
  };

  const companyOptions = companies.map((company) => ({ value: company.id, label: company.name }));
  const operatorOptions = filteredPersonnel.map((person) => ({
    value: person.id,
    label: `${person.first_name} ${person.last_name}`.trim(),
  }));
  const isEditing = Boolean(initialValues?.id);

  return (
    <Drawer
      title={isEditing ? 'Edit Machine' : 'New Machine'}
      width={680}
      onClose={onClose}
      open={open}
      destroyOnClose
      footer={(
        <Flex justify="flex-end" gap={8}>
          <Button onClick={onClose}>{'Cancel'}</Button>
          <Button action={isEditing ? 'edit' : 'add'} type="primary" loading={loading} onClick={() => form.submit()}>
            {isEditing ? 'Update' : 'Save'}
          </Button>
        </Flex>
      )}
    >
      <ConfigProvider theme={{ components: { Form: { itemMarginBottom: 12, verticalLabelPadding: '0 0 2px' } } }}>
        <Spin spinning={isLoadingLookups || loading}>
          <Form form={form} layout="vertical" size="small" onFinish={handleFinish}>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item
                  name="machine_type"
                  label={'Machine Type'}
                  rules={[{ required: true, message: 'Please select a machine type' }]}
                >
                  <Select
                    options={machineTypeOptions}
                    loading={isLoadingMachineTypes}
                    placeholder={isLoadingMachineTypes ? 'Loading...' : 'Select machine type'}
                    showSearch
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="license_plate"
                  label={'Plate / Serial No'}
                  rules={[
                    { required: true, message: 'Please enter plate or serial no' },
                    { transform: (value) => value?.trim() },
                  ]}
                >
                  <Input placeholder={'Enter plate or serial no'} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="make" label={'Make'} rules={[{ transform: (value) => value?.trim() || undefined }]}>
                  <Input placeholder={'Enter make (e.g. Caterpillar, Komatsu)'} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="model" label={'Model'} rules={[{ transform: (value) => value?.trim() || undefined }]}>
                  <Input placeholder={'Enter model (e.g. 320D, WA380)'} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="year"
                  label={'Year'}
                  rules={[{
                    validator: (_, value) => {
                      if (value && (Number.isNaN(Number(value)) || value < 1900 || value > new Date().getFullYear() + 1)) {
                        return Promise.reject(new Error('Please enter a valid year'));
                      }
                      return Promise.resolve();
                    },
                  }]}
                >
                  <InputNumber style={{ width: '100%' }} placeholder={'Enter year'} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="hours_counter"
                  label={'Hours Counter'}
                  rules={[
                    { required: true, message: 'Please enter current hours' },
                    { type: 'number', min: 0, message: 'Hours cannot be negative' },
                  ]}
                >
                  <InputNumber style={{ width: '100%' }} placeholder={'Enter current machine hours'} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="kilometer"
                  label={'Kilometer'}
                  rules={[{ type: 'number', min: 0, message: 'Kilometer cannot be negative' }]}
                >
                  <InputNumber style={{ width: '100%' }} placeholder={'Enter current kilometer'} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="fuel_type" label={'Fuel Type'}>
                  <Select options={fuelTypeOptions} placeholder={'Select fuel type'} allowClear />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="ownership_type"
                  label={'Ownership Type'}
                  rules={[{ required: true, message: 'Please select ownership type' }]}
                >
                  <Select options={ownershipOptions} placeholder={'Select ownership type'} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="company_id"
                  label={'Company'}
                  rules={[{ required: true, message: 'Please select a company' }]}
                >
                  <Select
                    options={companyOptions}
                    loading={isLoadingLookups}
                    onChange={handleCompanyChange}
                    showSearch
                    allowClear
                    placeholder={'Select company'}
                    filterOption={matchOption}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="operator_id" label={'Operator'}>
                  <Select
                    options={operatorOptions}
                    loading={isLoadingLookups}
                    showSearch
                    allowClear
                    disabled={!selectedCompanyId}
                    placeholder={selectedCompanyId
                      ? 'Select operator'
                      : 'Select company first'}
                    filterOption={matchOption}
                    notFoundContent={isLoadingLookups
                      ? <Spin size="small" />
                      : (selectedCompanyId
                        ? 'No personnel found for this company'
                        : 'Select company')}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="arrival_date"
                  label={'Arrival Date'}
                  rules={[{ required: true, message: 'Please enter the arrival date' }]}
                >
                  <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="departure_date"
                  label={'Departure Date'}
                  dependencies={['arrival_date']}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const arrival = getFieldValue('arrival_date');
                        if (!value || !arrival) return Promise.resolve();
                        if (dayjs(value).startOf('day').isBefore(dayjs(arrival).startOf('day'))) {
                          return Promise.reject(new Error('Departure date cannot be earlier than the arrival date'));
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    format="DD/MM/YYYY"
                    disabledDate={(current) => {
                      const arrival = form.getFieldValue('arrival_date');
                      if (!current || !arrival) return false;
                      return current.startOf('day').isBefore(dayjs(arrival).startOf('day'));
                    }}
                    onChange={(date) => {
                      const arrival = form.getFieldValue('arrival_date');
                      const isBeforeArrival = Boolean(
                        date && arrival && date.startOf('day').isBefore(dayjs(arrival).startOf('day'))
                      );
                      if (date && !isBeforeArrival) form.setFieldsValue({ status: 'Inactive' });
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="contract_reference_id"
                  label={'Contract'}
                  rules={[
                    { max: 50, message: 'Contract reference must be at most 50 characters' },
                    { transform: (value) => (typeof value === 'string' ? value.trim() || undefined : value) },
                  ]}
                >
                  <Input
                    readOnly
                    maxLength={50}
                    allowClear
                    placeholder={'Select a contract'}
                    addonAfter={(
                      <Button type="link" size="small" onClick={() => setContractModalOpen(true)}>
                        {'Select'}
                      </Button>
                    )}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="status"
                  label={'Machine Status'}
                  rules={[{ required: true, message: 'Please select status' }]}
                >
                  <Select options={statusOptions} placeholder={'Select status'} />
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left" style={{ margin: '4px 0 12px' }}>
              {'Machine Specifications'}
            </Divider>
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              {specRows.map((row, index) => (
                <Flex key={index} gap={8}>
                  <Input
                    value={row.key}
                    placeholder={'Specification key (e.g. Power, Capacity)'}
                    onChange={(event) => updateSpec(index, { key: event.target.value })}
                  />
                  <Input
                    value={row.value}
                    placeholder={'Specification value (e.g. 150 kW, 30 t)'}
                    onChange={(event) => updateSpec(index, { value: event.target.value })}
                  />
                  <Button
                    danger
                    icon={<MinusOutlined />}
                    onClick={() => setSpecRows((rows) => {
                      const next = rows.filter((_, rowIndex) => rowIndex !== index);
                      return next.length > 0 ? next : [EMPTY_SPEC];
                    })}
                  />
                </Flex>
              ))}
              <Button type="dashed" block icon={<PlusOutlined />} onClick={() => setSpecRows((rows) => [...rows, EMPTY_SPEC])}>
                {'Add Specification Row'}
              </Button>
            </Space>
          </Form>
          <ContractSelect
            open={contractModalOpen}
            value={contractReference ? { reference_id: contractReference } : undefined}
            onCancel={() => setContractModalOpen(false)}
            onOk={(selected) => {
              form.setFieldsValue({ contract_reference_id: selected.reference_id || undefined });
              setContractModalOpen(false);
            }}
          />
        </Spin>
      </ConfigProvider>
    </Drawer>
  );
};

export default MachConstMachineForm;
